<script lang="ts" setup>
import { onMounted } from 'vue'

const route = useRoute()

const { podcastId } = route.params as { podcastId: string }

const { data: podcast } = await usePodcast(podcastId)

if (!podcast.value) {
  throw showError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

if (podcast.value.identifier !== podcastId) {
  navigateTo(podcast.value.route)
}

useSeo({
  title: `Listen to ${podcast.value.title}`,
  description: `Tune in to ${podcast.value.title} and explore a world of captivating episodes. Catch up on your favorite podcasts and discover new shows today!`,
  image: podcast.value.metaLogo,
})

onMounted(() => {
  if (podcast.value) {
    if (podcast.value.external) {
      window.gtag('event', 'page_view', {
        content_group: 'Third Party Podcasts',
      })
    } else {
      window.gtag('event', 'page_view', {
        content_group: 'Zeno Podcasts',
      })
    }
  }
})
</script>

<template>
  <div v-if="podcast">
    <CoverImage :alt="podcast.title" :src="podcast.cover" />

    <div class="flex md:flex-row flex-col md:gap-8 gap-2 mt-8 mb-12">
      <div class="md:pl-8 pl-0 -mt-16 flex-none text-center">
        <PodcastLogo :alt="podcast.title" :src="podcast.logo" />
      </div>

      <section class="space-y-5">
        <div class="md:text-left text-center">
          <div v-if="podcast.author || podcast.explicit" class="flex items-center gap-2 text-sm text-zeno-text-light">
            <span v-if="podcast.explicit" title="Explicit content">
              <Icon class="" name="material-symbols:explicit-rounded" size="18" />
            </span>

            <span v-if="podcast.author" class="text-sm text-zeno-text-light">{{ podcast.author }}</span>
          </div>

          <h1 class="md:text-2xl text-lg font-semibold mb-2">
            {{ podcast.title }}
          </h1>

          <h2 v-if="podcast?.station">
            <NuxtLink :to="podcast?.station.route" class="block hover:underline md:text-lg">
              {{ podcast.station.title }}
            </NuxtLink>
          </h2>
        </div>

        <PodcastTags :podcast="podcast" />

        <LazyStationAdRectangle />

        <DescriptionText v-if="podcast.description" :text="podcast.description" />

        <div v-if="podcast.link">
          <NuxtLink
            :to="podcast.link"
            aria-label="Podcast website"
            class="px-4 py-1 bg-zeno-gray-light rounded-full inline-flex items-center"
            target="_blank"
          >
            <Icon name="bi:globe2" size="16" />
            <span class="ml-2">Website</span>
          </NuxtLink>
        </div>
      </section>
    </div>

    <div class="md:space-y-8 space-y-6">
      <div class="border-b border-zeno-gray-border flex space-x-4 md:justify-start justify-between">
        <button class="px-6 pb-4 text-lg border-b border-zeno-text flex items-center">
          Episodes
          <span class="bg-zeno-gray-light rounded-full text-zeno-gray text-xs p-1 ml-2 font-semibold">
            {{ podcast.episodeCount }}
          </span>
        </button>
        <!--        <button class="cursor-pointer px-6 pb-4 text-lg text-zeno-gray">Related</button>-->
      </div>

      <PodcastEpisodes :podcast="podcast" />
    </div>
  </div>
</template>

<style scoped></style>
